// Precondition: Custom element must be defined in DOM
// Postcondition: Renders a CSS Grid-based app layout with header, main, footer, and optional sidebar
class AppLayout extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this.render();
  }

  // Precondition: None
  // Postcondition: Returns true if sidebar attribute is present
  private hasSidebar(): boolean {
    return this.hasAttribute('sidebar');
  }

  private render(): void {
    const style = document.createElement('style');
    style.textContent = this.getStyles();

    const template = document.createElement('template');
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.appendChild(style);
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  private getStyles(): string {
    return `
      :host {
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto 1fr auto;
        min-height: 100vh;
        min-height: 100dvh; /* Modern mobile: accounts for dynamic browser chrome */
        align-content: start;
      }

      :host([sidebar]) {
        grid-template-columns: 250px 1fr;
      }

      .app-header {
        grid-column: 1 / -1;
        padding: 16px 20px;
      }

      aside {
        padding: 0 20px;
        overflow-y: auto;
      }

      .app-main {
        padding: 0 20px;
        overflow-y: auto;
        min-height: 0; /* Allow grid child to shrink for proper overflow */
      }

      /* Full-height content (chat, etc.) */
      .app-main > ::slotted(*) {
        height: 100%;
      }

      .app-footer {
        grid-column: 1 / -1;
        padding: 16px 20px;
      }

      @media (max-width: 768px) {
        :host([sidebar]) {
          grid-template-columns: 1fr;
        }

        aside {
          display: none;
        }
      }
    `;
  }

  private getTemplate(): string {
    const sidebarHtml = this.hasSidebar()
      ? '<aside><slot name="sidebar"></slot></aside>'
      : '';
    return `
      <header class="app-header"><slot name="header"></slot></header>
      ${sidebarHtml}
      <main class="app-main"><slot name="main"></slot></main>
      <footer class="app-footer"><slot name="footer"></slot></footer>
    `;
  }
}


customElements.define('ft-app-layout', AppLayout);
